import React, { useEffect, useState } from "react";
import moment from "moment";

const PrintableApplicationForm = (props) => {
  const [data, setData] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (props.applicationId) {
      Promise.all([
        props.apis.apiGetUnderWriterApplicationDetails({
          applicationNumber: props.applicationId,
        }),
        props.apis.apiGetUnderWriterLoanApplicationDetails({
          applicationNumber: props.applicationId,
        }).catch((err) => {
          console.error("Error fetching loan details", err);
          return null;
        })
      ])
        .then(([appDetails, loanDetails]) => {
          if (appDetails) {
            console.log("Printable data", appDetails);
            console.log("Loan details", loanDetails);
            setData({
              ...appDetails,
              sanctionedLoanIRR: loanDetails?.sanctionedLoanIRR || appDetails?.sanctionedLoanIRR,
            });
          }
        })
        .catch((err) => console.error("Error fetching printable data", err));
    }
  }, [props.applicationId]);

  useEffect(() => {
    let objectUrl = null;

    const fetchImage = async () => {
      if (data?.customer?.photoFileId) {
        try {
          const authToken = props?.$store?.auth;
          const baseUrl = window.endpoints ? window.endpoints[window.environment].api_base : '';
          const res = await fetch(`${baseUrl}/api/v1/files/download/${data.customer.photoFileId}`, {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          });
          if (res.ok) {
            const blob = await res.blob();
            objectUrl = URL.createObjectURL(blob);
            setImageUrl(objectUrl);
          }
        } catch (err) {
          console.error("Error fetching photo", err);
        }
      }
    };

    fetchImage();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [data?.customer?.photoFileId, props?.$store?.auth]);

  if (!data) return null;

  const { customer } = data;
  const commAddress = data.ApplicationAddressInfo?.find(a => a.addressType === "COMMUNICATION") || {};
  const permAddress = data.ApplicationAddressInfo?.find(a => a.addressType === "PERMANENT") || {};
  const salaryInfo = data.ApplicationSalaryInfo?.[0] || {};
  
  return (
    <div id="printable-application-form">
      <div className="print-header">
        <div className="print-logo">
          <img src={window.imagepaths?.loginWhiteBg || window.imagepaths?.logoHome || "/images/logo.png"} alt="AFI Logo" />
        </div>
        <div className="print-title">
          <h2>Customer Application Form</h2>
          <p>Application ID: {data.applicationId}</p>
        </div>
      </div>
      <hr />

      <div className="print-section">
        <h4 className="section-title">1. Customer Information</h4>
        <div className="d-flex justify-content-between d-print-flex" style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <div className="customer-info-left" style={{ flex: "1" }}>
            <table className="print-table">
              <tbody>
                <tr><th>Name:</th><td>{data.customerName}</td></tr>
                <tr><th>Email:</th><td>{customer?.emailAddress}</td></tr>
                <tr><th>Mobile:</th><td>{customer?.mobileNumber}</td></tr>
                <tr><th>Alt Mobile:</th><td>{customer?.alternateMobileNumber}</td></tr>
                <tr><th>DOB:</th><td>{customer?.dob ? moment(customer.dob).format("DD-MMM-YYYY") : "-"}</td></tr>
                <tr><th>PAN:</th><td>{customer?.panCardId}</td></tr>
                <tr><th>Gender:</th><td>{customer?.gender}</td></tr>
              </tbody>
            </table>
          </div>
          <div className="customer-info-right text-center" style={{ width: "150px", minWidth: "150px", marginLeft: "20px" }}>
            {imageUrl ? (
              <img 
                src={imageUrl} 
                alt="Customer Photo" 
                className="print-customer-photo" 
                style={{ width: "120px", height: "120px", objectFit: "cover", border: "1px solid #ccc", padding: "4px" }}
                onError={(e) => {
                  e.target.onerror = null; 
                  e.target.style.display = 'none'; 
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className="print-photo-placeholder" style={{ display: imageUrl ? "none" : "flex", width: "120px", height: "120px", border: "1px solid #ccc", alignItems: "center", justifyContent: "center", fontSize: "12px", color: "#666" }}>
              {customer?.photoFileId ? "Loading..." : "No Photo"}
            </div>
          </div>
        </div>
      </div>

      <div className="print-section mt-4">
        <h4 className="section-title">2. Application Details</h4>
        <table className="print-table w-100 table-bordered">
          <tbody>
            <tr>
              <th>Loan Amount:</th><td>₹{data.loanAmount || 0}</td>
              <th>Duration:</th><td>{data.duration || 0} Days</td>
            </tr>
            <tr>
              <th>Interest Rate:</th><td>{data.sanctionedLoanIRR || 0}%</td>
              <th>Channel:</th><td>{data.channel}</td>
            </tr>
            <tr>
              <th>App Type:</th><td>{data.customerApplicationType}</td>
              <th>DSA Name:</th><td>{data.dsaName || "N/A"}</td>
            </tr>
            <tr>
              <th>Reference 1:</th>
              <td>{data.reference1name} ({data.reference1relation}) - {data.reference1mobile}</td>
              <th>Reference 2:</th>
              <td>{data.reference2name} ({data.reference2relation}) - {data.reference2mobile}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="print-section mt-4">
        <h4 className="section-title">3. Address Details</h4>
        <div className="row">
          <div className="col-6">
            <h5 className="sub-title">Communication Address</h5>
            <p className="address-text">
              {commAddress.address1} {commAddress.address2} {commAddress.address3} <br/>
              {commAddress.city}, {commAddress.state} - {commAddress.pincode} <br/>
              <strong>Status:</strong> {commAddress.residenceStatus}
            </p>
          </div>
          <div className="col-6">
            <h5 className="sub-title">Permanent Address</h5>
            <p className="address-text">
              {permAddress.address1} {permAddress.address2} {permAddress.address3} <br/>
              {permAddress.city}, {permAddress.state} - {permAddress.pincode} <br/>
              <strong>Status:</strong> {permAddress.residenceStatus}
            </p>
          </div>
        </div>
      </div>

      <div className="print-section mt-4">
        <h4 className="section-title">4. Income & Bank Details</h4>
        <div className="row">
          <div className="col-6">
            <h5 className="sub-title">Employment Info</h5>
            <table className="print-table">
              <tbody>
                <tr><th>Company:</th><td>{salaryInfo.companyname}</td></tr>
                <tr><th>Net Income:</th><td>₹{salaryInfo.monthlyNetTakeSalary}</td></tr>
                <tr><th>Bank Name:</th><td>{salaryInfo.bankName}</td></tr>
              </tbody>
            </table>
          </div>
          <div className="col-6">
            <h5 className="sub-title">Bank Account Details</h5>
            <table className="print-table">
              <tbody>
                <tr><th>Holder Name:</th><td>{data.accountHolderName}</td></tr>
                <tr><th>Account No:</th><td>{data.accountNumber}</td></tr>
                <tr><th>Bank (IFSC):</th><td>{data.accountHolderBankName} ({data.accountHolderBankIFSC})</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* <div className="print-section mt-4">
        <h4 className="section-title">5. Additional Documents & Statements</h4>
        <div className="row">
          <div className="col-6">
            <h5 className="sub-title">Payslips</h5>
            <ul className="print-list">
              {data.applicationPayslip?.map((p, i) => (
                <li key={i}>{p.filename} ({moment(p.payslipDate).format("MMM-YYYY")})</li>
              ))}
              {(!data.applicationPayslip || data.applicationPayslip.length === 0) && <li>No payslips uploaded</li>}
            </ul>
          </div>
          <div className="col-6">
            <h5 className="sub-title">Bank Statements</h5>
            <ul className="print-list">
              {data.applicationbankStatement?.map((b, i) => (
                <li key={i}>{b.bankname} ({moment(b.startDate).format("MMM-YYYY")} to {moment(b.endDate).format("MMM-YYYY")})</li>
              ))}
              {(!data.applicationbankStatement || data.applicationbankStatement.length === 0) && <li>No bank statements uploaded</li>}
            </ul>
          </div>
        </div>
      </div> */}

    </div>
  );
};

export default constore(PrintableApplicationForm);
